import { LoadingButton } from "@mui/lab";
import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Icon,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    styled,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { Button } from "antd";
import { NoRecordField, setTitleCase } from "app/components/template/partials";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { deleteDocumentation, editDocumentation } from "utils/action";
import { toServerURL } from "utils/generator";
import { useDocumentationUIContext } from "../DocumentationUIContext";

const StyledTable = styled(Table)(() => ({
    width: "max-content",
    whiteSpace: "pre",
    "& thead": {
        "& tr": { "& th": { paddingLeft: 0, paddingRight: 0 } },
    },
    "& tbody": {
        "& tr": {
            "& td": {
                paddingLeft: 0,
                paddingTop: "2px",
                paddingBottom: 0,
            },
        },
    },
}));

export function DocumentationListTable() {
    const documentationUIContext = useDocumentationUIContext();
    const documentationUIProps = useMemo(() => {
        return {
            queryParams: documentationUIContext.queryParams,
            setQueryParams: documentationUIContext.setQueryParams,
            getListDocumentationData:
                documentationUIContext.getListDocumentationData,
            goToEditDocumentation: documentationUIContext.goToEditDocumentation,
            gotoCreateDocumentation:
                documentationUIContext.gotoCreateDocumentation,
        };
    }, [documentationUIContext]);
    const {
        queryParams,
        setQueryParams,
        getListDocumentationData,
        goToEditDocumentation,
    } = documentationUIProps;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [itemID, setItemID] = useState("");

    const handleClickDelete = (id) => {
        setShowDeleteDialog(true);
        setItemID(id);
    };

    const handleRequestDelete = async () => {
        setShowDeleteDialog(true);
        setLoading(true);

        // send to backend
        try {
            await deleteDocumentation(itemID);
            toast.success(setTitleCase("berhasil menghapus data pelanggan"));
            setQueryParams(queryParams);
        } catch (error) {
            toast.error(setTitleCase(error.response.data?.message));
        }
        setLoading(false);
        setShowDeleteDialog(false);
    };

    const handleClickEdit = (item) => {
        goToEditDocumentation(item);
    };

    const [checkboxStatus, setCheckboxStatus] = useState({});
    useEffect(() => {
        if (getListDocumentationData.data) {
            const initialCheckboxStatus = {};
            getListDocumentationData.data.forEach((item) => {
                initialCheckboxStatus[item.id] = item.is_show_documentation;
            });
            setCheckboxStatus(initialCheckboxStatus);
        }
    }, [getListDocumentationData.data]);

    const handleCheckboxClick = async (itemId) => {
        setLoading(true);

        const formData = new FormData();
        formData.append("set_show_documentation", true);
        formData.append("is_show_documentation", !checkboxStatus[itemId]);

        try {
            await editDocumentation(itemId, formData);
            toast.success(
                setTitleCase("berhasil setting pilihan tampilan dokumentasi")
            );
            setCheckboxStatus((prevStatus) => ({
                ...prevStatus,
                [itemId]: !prevStatus[itemId], // Ini akan mengganti antara true dan false saat checkbox diklik
            }));
        } catch (error) {
            toast.error(setTitleCase(error.response.data?.message));
        }
        setLoading(false);
    };

    return (
        <Box width="100%" overflow="auto">
            <TableContainer>
                <StyledTable className="table-container">
                    <TableHead className="table-header">
                        <TableRow>
                            <TableCell
                                className="table-content-xs text-white"
                                align="center"
                            >
                                No
                            </TableCell>
                            <TableCell
                                className="table-content-xs text-white"
                                align="center"
                            >
                                Tampilkan di Homepage ?
                            </TableCell>
                            <TableCell
                                className="table-content-sm text-white"
                                align="center"
                            >
                                Judul
                            </TableCell>
                            <TableCell
                                className="table-content-xlg text-white"
                                align="center"
                            >
                                Konten Dokumentasi
                            </TableCell>
                            <TableCell
                                className="table-content-md text-white"
                                align="center"
                            >
                                Foto
                            </TableCell>
                            <TableCell
                                className="table-content-md text-white"
                                align="center"
                            >
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getListDocumentationData.isFulfilled &&
                            !getListDocumentationData.isLoading &&
                            getListDocumentationData.data &&
                            getListDocumentationData.data
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((item, index) => (
                                    <TableRow hover key={item.id}>
                                        <TableCell
                                            className="table-content-xs"
                                            align="center"
                                        >
                                            {NoRecordField(
                                                index + 1 + page * rowsPerPage
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-xs"
                                            align="center"
                                        >
                                            <Checkbox
                                                checked={
                                                    checkboxStatus[item.id] ??
                                                    false
                                                } // Menggunakan nilai dari checkboxStatus
                                                color="primary"
                                                onClick={() =>
                                                    handleCheckboxClick(item.id)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            className="table-content-xs"
                                            align="center"
                                        >
                                            {item.title}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="center"
                                        >
                                            {item.content}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="center"
                                        >
                                            <img
                                                src={toServerURL(
                                                    item.photo_url
                                                )}
                                                alt={item.title}
                                                style={{
                                                    maxWidth: "150px",
                                                    maxHeight: "150px",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="center"
                                        >
                                            <IconButton
                                                onClick={() =>
                                                    handleClickEdit(item)
                                                }
                                            >
                                                <Icon
                                                    className="icon"
                                                    sx={{ color: green[500] }}
                                                >
                                                    edit
                                                </Icon>
                                            </IconButton>
                                            <IconButton
                                                onClick={() =>
                                                    handleClickDelete(item.id)
                                                }
                                            >
                                                <Icon
                                                    className="icon"
                                                    sx={{ color: red[500] }}
                                                >
                                                    delete
                                                </Icon>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                    </TableBody>
                </StyledTable>
                <TablePagination
                    sx={{ px: 2 }}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    page={page}
                    count={
                        getListDocumentationData.count
                            ? getListDocumentationData.count
                            : 0
                    }
                    onPageChange={handleChangePage}
                    rowsPerPageOptions={[5, 10, 20]}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    nextIconButtonProps={{ "aria-label": "Next Page" }}
                    backIconButtonProps={{ "aria-label": "Previous Page" }}
                />
            </TableContainer>

            <Dialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Konfirmasi Hapus"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Apakah Anda yakin ingin menghapus dokumentasi ini?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowDeleteDialog(false)}
                        color="error"
                    >
                        Batal
                    </Button>
                    <LoadingButton
                        onClick={() => {
                            handleRequestDelete();
                        }}
                        autoFocus
                        loading={isLoading}
                        color="error"
                        variant="contained"
                    >
                        Hapus
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
